import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaPhoneAlt, FaUser, FaIdCard, FaBolt, FaBullhorn, FaBoxOpen, FaHashtag,
         FaCalendarAlt, FaClock, FaSitemap, FaCheckCircle, FaSearch, FaTimes } from 'react-icons/fa';
import { capturarFusion, listarAsesoras, asignarFusion, buscarPorId, listarAsignacionesFusion, eliminarAsignacion, buscarPrecargado } from '../../servicios/Fusion';
import '../home/home.css';   // look de "Leads" (clases lead-*)
import '../fusion/fusion.css'; // inputs del formulario (clases fu-*)
import './fusion2.css';

const VACIO = {
    telefono: '', id_fusion: '', nombre_fusion: '', ci_fusion: '',
    // Canal/Producto fijos por defecto (la mayoría de leads son WEB / portabilidad);
    // quedan ocultos pero editables en "Campos avanzados".
    canal: 'WEB', campania: '', producto: 'portabilidad',
    fecha_creacion: '', hora_creacion: '',
};

// Estado del CRM -> color del badge (mismo criterio visual que "Leads").
const estadoBadge = (estado) => {
    const e = (estado || '').toLowerCase();
    if (e.includes('no llamado')) return 'lead-badge--info';       // No Llamado -> azul
    if (e.includes('venta') || e.includes('completa')) return 'lead-badge--success';
    if (e.includes('interesado') && !e.includes('no interesado')) return 'lead-badge--info';
    if (e.includes('no contesta') || e.includes('buzón') || e.includes('buzon') || e.includes('volver')) return 'lead-badge--warning';
    if (e.includes('no interesado') || e.includes('fuera') || e.includes('colg') || e.includes('desist')) return 'lead-badge--danger';
    return 'lead-badge--default';   // gris (Agendado, Se acercó a CAC, etc.)
};

export default function Fusion2() {
    // ---- Listado ----
    const [lista, setLista] = useState([]);
    const [cargandoLista, setCargandoLista] = useState(false);
    const [busqueda, setBusqueda] = useState('');
    const [limite] = useState(200);

    // ---- Formulario (estático) + resultado (modal) ----
    const [d, setD] = useState(VACIO);
    const [mostrarAvanzados, setMostrarAvanzados] = useState(false);
    const [cargando, setCargando] = useState(false);
    const [resultado, setResultado] = useState(null);   // presencia => modal abierto
    const [asesoras, setAsesoras] = useState([]);
    const [auto, setAuto] = useState(null);   // { gestor_id, agent, modo, usar_config }
    const [modo, setModo] = useState('auto');
    const [gestorManual, setGestorManual] = useState('');
    const [asignando, setAsignando] = useState(false);
    const [asignado, setAsignado] = useState(null);
    const [aviso, setAviso] = useState(null);
    const [yaEnBitacora, setYaEnBitacora] = useState(null);

    const cargarLista = async (q = '') => {
        setCargandoLista(true);
        try { setLista(await listarAsignacionesFusion(limite, q)); }
        catch (ex) { toast.error(ex.message); }
        finally { setCargandoLista(false); }
    };

    useEffect(() => {
        listarAsesoras().then((d) => { setAsesoras(d.asesoras || []); setAuto(d.auto || null); }).catch(() => {});
        setD((p) => ({ ...p, fecha_creacion: hoyStr() }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Carga + búsqueda en TODA la bitácora (servidor), no solo en las cargadas.
    // Debounce: la carga inicial (busqueda='') y cada tecleo pasan por aquí.
    useEffect(() => {
        const t = setTimeout(() => { cargarLista(busqueda); }, 350);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [busqueda]);

    // ---- Eliminar lead (CRM + bitácora) — solo No Llamado / No Interesado ----
    const [aEliminar, setAEliminar] = useState(null);   // fila a eliminar (abre confirmación)
    const [eliminando, setEliminando] = useState(false);
    const puedeEliminar = (estado) => {
        const e = (estado || '').toLowerCase();
        return e === 'no llamado' || e === 'no interesado';
    };
    const confirmarEliminar = async () => {
        if (!aEliminar) return;
        setEliminando(true);
        try {
            await eliminarAsignacion(aEliminar.telefono);
            toast.success(`Lead ${aEliminar.telefono} eliminado`);
            setLista((prev) => prev.filter((x) => x.telefono !== aEliminar.telefono));
            setAEliminar(null);
        } catch (ex) { toast.error(ex.message); }
        finally { setEliminando(false); }
    };

    const pad = (x) => String(x).padStart(2, '0');
    const hoyStr = () => { const n = new Date(); return `${n.getFullYear()}-${pad(n.getMonth()+1)}-${pad(n.getDate())}`; };
    const fechaCorta = (v) => { const f = String(v || '').slice(0, 10).split('-'); return f.length === 3 ? `${f[2]}/${f[1]}/${f[0]}` : String(v || ''); };

    // ---- Handlers del formulario (misma lógica que la pantalla actual) ----
    const set = (k) => (e) => {
        const v = e.target.value;
        if (k === 'id_fusion' && resultado) {
            setD({ ...VACIO, id_fusion: v, fecha_creacion: hoyStr() });
            setResultado(null); setAsignado(null); setAviso(null); setYaEnBitacora(null);
            setModo('auto'); setGestorManual('');
            return;
        }
        setD({ ...d, [k]: v });
    };

    const onBlurId = async () => {
        const id = d.id_fusion.trim();
        if (id === '') { setYaEnBitacora(null); return; }
        const dat = await buscarPorId(id);
        setYaEnBitacora(dat);
        if (!dat) return;
        const datos = {
            ...d,
            telefono: dat.telefono || d.telefono,
            ci_fusion: dat.ci_fusion || '',
            nombre_fusion: dat.nombre_fusion || '',
            canal: dat.canal || 'WEB',
            campania: dat.campania_fusion || '',
            producto: dat.producto || 'portabilidad',
            fecha_creacion: dat.fecha_creacion_fusion || hoyStr(),
            hora_creacion: dat.hora_creacion_fusion || '',
        };
        setD(datos);
        verificar(datos);
    };


    // "Capturar" = verificar contra Claro y ABRIR el modal con el resultado + la asignación.
    const verificar = async (datos = d) => {
        const tel = datos.telefono.replace(/\D/g, '');
        if (tel.length !== 9 && tel.length !== 10) { toast.error('El teléfono debe tener 9 o 10 dígitos.'); return; }
        if (datos.id_fusion.trim() === '') { toast.error('El Id Fusión es obligatorio.'); return; }
        setCargando(true); setResultado(null); setAsignado(null); setModo('auto'); setGestorManual('');
        try {
            const res = await capturarFusion(datos);
            setResultado(res);   // abre el modal
            toast.success('Lead capturado y verificado');
        } catch (ex) { toast.error(ex.message); } finally { setCargando(false); }
    };

    const asignar = async (confirmar = false) => {
        if (modo === 'manual' && !gestorManual) { toast.warn('Elige una asesora.'); return; }
        setAsignando(true);
        try {
            const res = await asignarFusion(resultado.id_bitacora, modo, modo === 'manual' ? Number(gestorManual) : undefined, confirmar);
            if (res.header === 'CONFIRMAR') { setAviso(res); return; }
            setAviso(null);
            toast.success(res.mensaje + ' — listo para el siguiente lead');
            nuevo();            // limpia el formulario y CIERRA el modal (resultado=null)
            cargarLista();      // refresca el listado
        } catch (ex) { setAviso(null); toast.error(ex.message); } finally { setAsignando(false); }
    };

    // Limpia todo. Al poner resultado=null, el modal se cierra.
    const nuevo = () => { setD({ ...VACIO, fecha_creacion: hoyStr() }); setResultado(null); setAsignado(null); setAviso(null); setYaEnBitacora(null); setModo('auto'); setGestorManual(''); };

    // Cerrar el modal sin perder lo escrito en el formulario (solo descarta el resultado).
    const cerrarModal = () => { setResultado(null); setAsignado(null); setAviso(null); setModo('auto'); setGestorManual(''); };

    // ---- Reasignar desde el listado (cualquier fila) ----
    // Reusa el MISMO modal de asignación: trae los datos de la bitácora por teléfono para
    // recapturar (nuevo id_bitacora + estado real del CRM) y abre el modal con "Reasignar a".
    // El backend ya bloquea solo las ventas cerradas; el resto se puede reasignar aunque esté
    // en No Llamado u otro estado.
    const reasignarFila = async (r) => {
        const tel = String(r.telefono || '').replace(/\D/g, '');
        if (tel.length !== 9 && tel.length !== 10) { toast.error('El número de esta fila no es válido.'); return; }
        const dat = (await buscarPrecargado(tel)) || {};
        // La fila viene del listado (asignado=1), así el modal muestra "Reasignar a".
        setYaEnBitacora({ ...dat, asignado: 1 });
        const datos = {
            ...VACIO,
            telefono:       dat.telefono || r.telefono,
            id_fusion:      dat.id_fusion || r.id_fusion || '',
            ci_fusion:      dat.ci_fusion || '',
            nombre_fusion:  dat.nombre_fusion || r.nombre_fusion || '',
            canal:          dat.canal || 'WEB',
            campania:       dat.campania_fusion || r.campania_fusion || '',
            producto:       dat.producto || 'portabilidad',
            fecha_creacion: dat.fecha_creacion_fusion || hoyStr(),
            hora_creacion:  dat.hora_creacion_fusion || '',
        };
        if (datos.id_fusion.trim() === '') { toast.error('Esta fila no tiene Id Fusión; recaptúrala desde el formulario.'); return; }
        setD(datos);
        verificar(datos);   // abre el modal de asignación/reasignación
    };

    const yaAsignada = Number(yaEnBitacora?.asignado) === 1;

    const badge = (enc) => {
        if (enc === 'CLARO-PRE') return <span className="fu-badge fu-pre">Cliente Claro · PREPAGO</span>;
        if (enc === 'CLARO-POST') return <span className="fu-badge fu-post">Cliente Claro · POSTPAGO</span>;
        return <span className="fu-badge fu-no">No está en la base de Claro</span>;
    };

    const campo = (Icon, label, key, opts = {}) => (
        <label className={`fu-field ${opts.req ? 'fu-req' : ''}`}>
            <span><Icon /> {label}</span>
            {opts.datalist
                ? <>
                    <input value={d[key]} onChange={set(key)} list={`dl2-${key}`} placeholder={opts.ph || 'Elige o escribe…'} disabled={cargando} autoComplete="off" />
                    <datalist id={`dl2-${key}`}>{opts.datalist.map((o) => <option key={o} value={o} />)}</datalist>
                  </>
                : <input type={opts.type || 'text'} step={opts.type === 'time' ? '1' : undefined} value={d[key]} onChange={set(key)} onBlur={opts.onBlur} placeholder={opts.ph || ''} disabled={cargando} />}
            {opts.hint && <small>{opts.hint}</small>}
        </label>
    );

    // ---- Filtro del listado ----
    const q = busqueda.replace(/\D/g, '');
    const filtradas = lista.filter((r) =>
        q === '' ? true : (r.telefono || '').includes(q) || (r.id_fusion || '').includes(busqueda.trim())
    );

    return (
        <div className="home-wrap f2-page">
            <ToastContainer position="top-right" autoClose={4000} />

            {/* ---- Card 1: formulario ESTÁTICO de captura (sin header, el título va en el topbar) ---- */}
            <section className="lead-card">
                <div className="lead-card__body f2-form">
                    <div className="fu-grid">
                        {/* Fila 1 */}
                        {campo(FaHashtag, 'Id Fusión', 'id_fusion', { req: true, ph: 'Ingresar id fusión', onBlur: onBlurId, hint: 'si ya está en la bitácora, carga sus datos' })}
                        {campo(FaPhoneAlt, 'Teléfono', 'telefono', { req: true, ph: 'Ingresar teléfono', hint: '9 dígitos — el sistema le antepone el 0' })}
                        {campo(FaUser, 'Nombre Fusión', 'nombre_fusion')}
                        {campo(FaIdCard, 'CI Fusión', 'ci_fusion', { hint: 'cédula que envía Fusión' })}
                        {/* Fila 2 */}
                        {campo(FaBullhorn, 'Campaña', 'campania', { datalist: ['Evolution-Portabilidad-Pospago-Asistido', 'WebPublica'], hint: 'elige o escribe otra' })}
                        {campo(FaCalendarAlt, 'Fecha creación', 'fecha_creacion', { type: 'date' })}
                        {campo(FaClock, 'Hora creación', 'hora_creacion', { ph: '12:00:00', hint: 'copia y pega de Fusión (hh:mm:ss)' })}
                        <div className="fu-field f2-btn-cell">
                            <span>&nbsp;</span>
                            <button className="fu-btn fu-btn--main" onClick={() => verificar()} disabled={cargando}>
                                <FaSearch /> {cargando ? 'Verificando…' : 'Capturar'}
                            </button>
                        </div>
                    </div>

                    {/* Campos fijos ocultos (Canal = WEB, Producto = portabilidad), editables si hace falta */}
                    <div className="f2-avanzados">
                        <button type="button" className="f2-avz-toggle" onClick={() => setMostrarAvanzados((v) => !v)} disabled={cargando}>
                            <i className={`bi ${mostrarAvanzados ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                            {mostrarAvanzados
                                ? 'Ocultar canal y producto'
                                : `Canal y producto (fijos: ${d.canal || 'WEB'} / ${d.producto || 'portabilidad'})`}
                        </button>
                        {mostrarAvanzados && (
                            <div className="fu-grid f2-avz-grid">
                                {campo(FaSitemap, 'Canal', 'canal', { datalist: ['WEB', 'Google Leads'], hint: 'por defecto WEB' })}
                                {campo(FaBoxOpen, 'Producto', 'producto', { datalist: ['portabilidad'], hint: 'por defecto portabilidad' })}
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* ---- Card 2: listado de asignaciones ---- */}
            <section className="lead-card">
                <div className="lead-card__header lead-card__header--toolbar">
                    <span className="lead-card__title-wrap"><i className="bi bi-table"></i>
                        <span className="lead-card__title">Leads Fusión asignados</span></span>
                    <div className="lead-card__toolbar">
                        <div className="lead-search">
                            <i className="bi bi-search"></i>
                            <input value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Buscar por número o Id" maxLength={12} />
                        </div>
                        <button className="btn lead-btn-primary" onClick={() => cargarLista(busqueda)} disabled={cargandoLista}>
                            <i className="bi bi-arrow-clockwise"></i> {cargandoLista ? 'Cargando…' : 'Actualizar'}
                        </button>
                    </div>
                </div>
                <div className="lead-card__body">
                    <div className="table-responsive lead-lista-scroll">
                        <table className="table lead-table align-middle mb-0">
                            <thead>
                                <tr>
                                    <th>Número</th><th>Id Fusión</th><th>Nombre</th><th>Campaña</th>
                                    <th>Asesora</th><th>Estado</th><th>Fecha asignación</th>
                                    <th className="text-end">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtradas.map((r, i) => (
                                    <tr key={(r.telefono || '') + '-' + (r.id_fusion || '') + '-' + i}>
                                        <td className="fw-semibold">{r.telefono}</td>
                                        <td>{r.id_fusion || '—'}</td>
                                        <td><span className="f2-trunc" title={r.nombre_fusion || ''}>{r.nombre_fusion || '—'}</span></td>
                                        <td><span className="f2-trunc" title={r.campania_fusion || ''}>{r.campania_fusion || '—'}</span></td>
                                        <td>{r.asesor || '—'}</td>
                                        <td><span className={`lead-badge ${estadoBadge(r.estado)}`}>{r.estado || '—'}</span></td>
                                        <td>{fechaCorta(r.fecha_asignacion)}</td>
                                        <td className="text-end">
                                            <div className="lead-actions">
                                                <button
                                                    type="button"
                                                    className="lead-action lead-action--reasig"
                                                    title="Reasignar a otra asesora"
                                                    onClick={() => reasignarFila(r)}
                                                >
                                                    <i className="bi bi-arrow-left-right"></i>
                                                </button>
                                                {puedeEliminar(r.estado) && (
                                                    <button
                                                        type="button"
                                                        className="lead-action lead-action--danger"
                                                        title="Eliminar lead (CRM + bitácora)"
                                                        onClick={() => setAEliminar(r)}
                                                    >
                                                        <i className="bi bi-trash"></i>
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {filtradas.length === 0 && (
                                    <tr><td colSpan="8" className="text-center lead-empty">
                                        {cargandoLista ? 'Cargando…' : 'No hay asignaciones de Fusión.'}
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            {/* ---- Modal: SOLO el resultado de la captura + la asignación ---- */}
            {resultado && (
                <div className="f2-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !asignando) cerrarModal(); }}>
                    <div className="f2-modal f2-modal--result">
                        <div className="f2-modal__head">
                            <div className="f2-modal__title"><FaBolt /> Verificación y asignación</div>
                            <button className="f2-modal__close" onClick={cerrarModal} disabled={asignando}><FaTimes /></button>
                        </div>

                        <div className="f2-modal__body">
                            <div className="fu-result">
                                <div className="fu-result__head">
                                    <span className="fu-tel-num">{resultado.telefono}</span>
                                    {badge(resultado.encontrado)}
                                </div>

                                {(resultado.estado_crm || Number(yaEnBitacora?.asignado) === 1) && (
                                    <div className={`fu-estado ${resultado.estado_crm?.bloquea ? 'fu-estado--bloquea' : ''}`}>
                                        <div className="fu-estado__top">
                                            <span>{resultado.estado_crm ? 'Estado en el CRM' : 'Esta solicitud'}</span>
                                            <strong>{resultado.estado_crm ? resultado.estado_crm.estado : 'Ya asignada'}</strong>
                                        </div>
                                        <span className="fu-estado__pie">
                                            {Number(yaEnBitacora?.asignado) === 1
                                                ? `Ya asignada${yaEnBitacora.fecha_asignacion ? ' el ' + fechaCorta(yaEnBitacora.fecha_asignacion) : ''}${yaEnBitacora.asesor ? ' a ' + yaEnBitacora.asesor : ''}`
                                                : `Última gestión${resultado.estado_crm?.asesora ? ' de ' + resultado.estado_crm.asesora : ''}${resultado.estado_crm?.hoy ? ' · hoy' : (resultado.estado_crm?.fecha ? ' · ' + fechaCorta(resultado.estado_crm.fecha) : '')}`}
                                            {resultado.estado_crm?.bloquea ? ' · no se puede reasignar' : ''}
                                        </span>
                                    </div>
                                )}

                                {resultado.datos_claro && (
                                    <div className="fu-datos">
                                        <h4>Datos de Claro</h4>
                                        <div className="fu-datos-grid">
                                            <div><span>Nombre</span><strong>{resultado.datos_claro.nombre || '—'}</strong></div>
                                            <div><span>Cédula</span><strong>{resultado.datos_claro.identificacion || '—'}</strong></div>
                                            <div><span>Tarifa</span><strong>{resultado.datos_claro.tarifa || '—'}</strong></div>
                                            <div><span>Ciudad</span><strong>{resultado.datos_claro.ciudad || '—'}</strong></div>
                                            <div className="fu-datos-full"><span>Plan</span><strong>{resultado.datos_claro.plan || '—'}</strong></div>
                                        </div>
                                    </div>
                                )}

                                {!asignado && resultado.estado_crm?.bloquea ? (
                                    <div className="fu-asig">
                                        <h4>Nada que reasignar</h4>
                                        <p className="fu-asig__nota">
                                            Este número ya es venta{resultado.estado_crm.asesora ? ` de ${resultado.estado_crm.asesora}` : ''}. Queda cerrado.
                                        </p>
                                        <button className="fu-btn fu-btn--sec" onClick={cerrarModal}>Cerrar</button>
                                    </div>
                                ) : !asignado ? (
                                    <div className="fu-asig">
                                        <h4>{yaAsignada ? 'Reasignar a' : 'Asignar a'}</h4>
                                        <div className="fu-asig__modo">
                                            <button className={`fu-asig__op ${modo==='auto'?'activo':''}`} onClick={()=>setModo('auto')} disabled={asignando}>
                                                Automática<small>{
                                                    auto
                                                        ? (auto.modo === 'rotativo' ? 'Rotativo (reparte)'
                                                            : auto.modo === 'menor_carga' ? 'Menor carga'
                                                            : (auto.agent || 'Mercedes Pluas'))
                                                        : 'Mercedes Pluas'
                                                }</small></button>
                                            <button className={`fu-asig__op ${modo==='manual'?'activo':''}`} onClick={()=>setModo('manual')} disabled={asignando}>
                                                Manual<small>elegir asesora</small></button>
                                        </div>
                                        {modo==='manual' && (
                                            <select className="fu-asig__sel" value={gestorManual} onChange={(e)=>setGestorManual(e.target.value)} disabled={asignando}>
                                                <option value="">Elige una asesora…</option>
                                                {asesoras.map(a=><option key={a.id_solicitudes} value={a.id_solicitudes}>{a.agent}</option>)}
                                            </select>
                                        )}
                                        {aviso ? (
                                            <div className="fu-aviso">
                                                <h5>Este número ya fue gestionado</h5>
                                                <div className="fu-aviso__grid">
                                                    <div><span>Estado</span><strong>{aviso.estado_previo?.estado || '—'}</strong></div>
                                                    <div><span>Asesora</span><strong>{aviso.estado_previo?.asesora || '—'}</strong></div>
                                                    <div className="fu-datos-full"><span>Última gestión</span>
                                                        <strong>{aviso.estado_previo?.hoy ? 'Hoy' : (aviso.estado_previo?.fecha || '—')}</strong></div>
                                                </div>
                                                <div className="fu-aviso__btns">
                                                    <button className="fu-btn fu-btn--asig" onClick={() => asignar(true)} disabled={asignando}>
                                                        {asignando ? 'Reasignando…' : 'Reasignar igual'}
                                                    </button>
                                                    <button className="fu-btn fu-btn--sec" onClick={() => setAviso(null)} disabled={asignando}>
                                                        Cancelar
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <button className="fu-btn fu-btn--asig" onClick={() => asignar(false)} disabled={asignando}>
                                                {asignando ? 'Asignando…' : (yaAsignada ? 'Reasignar' : 'Registrar y asignar')}
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    <div className="fu-ok">
                                        <FaCheckCircle />
                                        <p>{asignado.mensaje}</p>
                                        <button className="fu-btn fu-btn--sec" onClick={nuevo}>Listo</button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ---- Confirmación de eliminación ---- */}
            {aEliminar && (
                <div className="f2-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !eliminando) setAEliminar(null); }}>
                    <div className="f2-modal f2-modal--confirm">
                        <div className="f2-modal__head f2-modal__head--danger">
                            <div className="f2-modal__title"><i className="bi bi-trash"></i> Eliminar lead</div>
                            <button className="f2-modal__close" onClick={() => setAEliminar(null)} disabled={eliminando}><FaTimes /></button>
                        </div>
                        <div className="f2-modal__body">
                            <p className="f2-del-q">¿Eliminar el lead <strong>{aEliminar.telefono}</strong>?</p>
                            <p className="f2-del-sub">
                                Asesora: {aEliminar.asesor || '—'} · Estado: {aEliminar.estado || '—'}.<br />
                                Se borra del <strong>CRM</strong> (asignación) y de la <strong>bitácora de Fusión</strong>. No se puede deshacer.
                            </p>
                            <div className="f2-del-acciones">
                                <button className="fu-btn fu-btn--sec" onClick={() => setAEliminar(null)} disabled={eliminando}>Cancelar</button>
                                <button className="fu-btn f2-del-btn" onClick={confirmarEliminar} disabled={eliminando}>
                                    {eliminando ? 'Eliminando…' : 'Eliminar'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
